import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays, Check, ChevronRight, Clock3, Download, ExternalLink, Mail,
  Pause, Play, RefreshCw, SkipForward, Sparkles
} from 'lucide-react';
import { getContentFor } from '../data/verseDeliveryContent';
import { downloadIcsCalendar, getGoogleCalendarUrl, toDateKey } from '../services/prayerCalendarService';
import { pauseSubscription, updateDelivery } from '../services/verseDeliveryService';
import ScreenLayout from './ScreenLayout';
import useDeliveryState from '../hooks/useDeliveryState';

const formatMonth = (date) => new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
const formatDay = (date) => new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(date);
const formatTime = (date) => new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);

const toCalendarEvent = (delivery) => {
  const start = new Date(delivery.scheduledAt);
  const end = new Date(start.getTime() + (15 * 60 * 1000));
  return {
    id: `verse-delivery-${delivery.id}`,
    date: toDateKey(start),
    start: start.toISOString(),
    end: end.toISOString(),
    title: `Verse Delivery · ${delivery.content.reference}`,
    description: `Your scheduled delivery arrives at ${formatTime(start)}.`,
    scripture: `${delivery.content.reference} — ${delivery.content.text}`
  };
};

export default function DeliveryCalendar() {
  const { deliveries, subscriptions, loading, error, setState, reload } = useDeliveryState();
  const [selectedId, setSelectedId] = useState(null);
  const [exported, setExported] = useState(false);
  const [actionError, setActionError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedId && deliveries[0]?.id) setSelectedId(deliveries[0].id);
    if (selectedId && !deliveries.some((delivery) => delivery.id === selectedId)) {
      setSelectedId(deliveries[0]?.id || null);
    }
  }, [deliveries, selectedId]);

  const selected = deliveries.find((delivery) => delivery.id === selectedId);
  const groups = useMemo(() => deliveries.reduce((acc, delivery) => {
    const key = new Date(delivery.scheduledAt).toDateString();
    (acc[key] ||= []).push(delivery);
    return acc;
  }, {}), [deliveries]);
  const calendarEvents = useMemo(() => deliveries.filter((delivery) => delivery.status !== 'skipped').map(toCalendarEvent), [deliveries]);
  const selectedCalendarEvent = selected ? toCalendarEvent(selected) : null;

  const mutate = async (changes) => {
    if (!selected) return;
    setSaving(true);
    setActionError('');
    try {
      setState(await updateDelivery(selected.id, changes));
    } catch (mutationError) {
      setActionError(mutationError.message || 'That delivery could not be updated.');
    } finally {
      setSaving(false);
    }
  };
  const swap = async () => {
    const current = Number(selected.content.dayIndex || 0);
    const nextContent = getContentFor(selected.content.topicTags[0], current);
    await mutate({ contentItemId: nextContent.id });
  };
  const pause = async () => {
    const id = selected?.subscriptionId || subscriptions[0]?.id;
    if (!id) return;
    setSaving(true);
    setActionError('');
    try {
      const isPaused = subscriptions.find((item) => item.id === id)?.status === 'paused';
      setState(await pauseSubscription(id, isPaused));
    } catch (pauseError) {
      setActionError(pauseError.message || 'That schedule could not be updated.');
    } finally {
      setSaving(false);
    }
  };
  const exportSchedule = () => {
    downloadIcsCalendar(calendarEvents, 'verse-delivery-schedule.ics');
    setExported(true);
    window.setTimeout(() => setExported(false), 2400);
  };

  const paused = subscriptions.find((item) => item.id === selected?.subscriptionId)?.status === 'paused';
  if (loading) {
    return <ScreenLayout title="Verse Delivery Calendar" eyebrow="See what’s coming" icon={CalendarDays} tone="sand"><div className="vd-state-panel" role="status">Loading your calendar…</div></ScreenLayout>;
  }

  if (error) {
    return (
      <ScreenLayout title="Verse Delivery Calendar" eyebrow="See what’s coming" icon={CalendarDays} tone="sand">
        <div className="vd-state-panel" role="alert">
          <h1>Your calendar could not be loaded.</h1><p>{error}</p>
          <button onClick={() => reload().catch(() => undefined)}><RefreshCw size={17} /> Try again</button>
        </div>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout title="Verse Delivery Calendar" eyebrow="See what’s coming" icon={CalendarDays} tone="sand">
      <div className="vd-calendar">
        <header className="vd-calendar-hero">
          <div><p>Next 14 deliveries</p><h1>Your peaceful rhythm,<br />already waiting.</h1><span>Review, adjust, or carry your schedule into the calendar you already use.</span></div>
          <div className="vd-calendar-actions">
            <button className="vd-calendar-export" onClick={exportSchedule} disabled={!calendarEvents.length}>
              {exported ? <Check size={17} /> : <Download size={17} />}{exported ? 'Calendar downloaded' : 'Export to Google Calendar'}
            </button>
            <button onClick={pause} disabled={saving}><Pause size={17} />{paused ? 'Resume schedule' : 'Pause schedule'}</button>
          </div>
        </header>

        {actionError ? <p className="vd-flow-error" role="alert">{actionError}</p> : null}

        {deliveries.length ? <>
          <section className="vd-month-strip">
            <header><div><small>Your two-week path</small><strong>{formatMonth(new Date())}</strong></div><span><i className="scheduled" /> Scheduled <i className="paused" /> Paused <i className="skipped" /> Skipped</span></header>
            <div>{deliveries.map((delivery) => {
              const date = new Date(delivery.scheduledAt);
              return <button title={`${delivery.content.reference} · ${delivery.status}`} className={`${delivery.status} ${delivery.id === selectedId ? 'selected' : ''}`} onClick={() => setSelectedId(delivery.id)} key={delivery.id}>
                <small>{new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date)}</small><strong>{date.getDate()}</strong>
                {delivery.status === 'skipped' ? <SkipForward size={14} /> : delivery.status === 'paused' ? <Pause size={14} /> : <Play size={14} />}
              </button>;
            })}</div>
          </section>

          <div className="vd-calendar-layout">
            <section className="vd-agenda" aria-label="Scheduled Verse Deliveries">
              <header><span><Sparkles size={16} /> Upcoming deliveries</span><small>{calendarEvents.length} ready</small></header>
              {Object.entries(groups).map(([date, dateDeliveries]) => <div className="vd-agenda-day" key={date}>
                <time>{formatDay(new Date(date))}</time>
                {dateDeliveries.map((delivery) => <button className={delivery.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(delivery.id)} key={delivery.id}>
                  <span><Clock3 size={15} />{formatTime(new Date(delivery.scheduledAt))}</span><strong>{delivery.content.reference}</strong><em>{delivery.status}</em><ChevronRight size={17} />
                </button>)}
              </div>)}
            </section>

            {selected ? <aside className="vd-delivery-detail">
              <div className="vd-delivery-detail-method"><Mail size={15} /><span>Queued delivery</span><em>{selected.status}</em></div>
              <p>Selected delivery</p><h2>{selected.content.reference}</h2><blockquote>“{selected.content.text}”</blockquote>
              <time>{new Intl.DateTimeFormat('en-US', { dateStyle: 'full', timeStyle: 'short' }).format(new Date(selected.scheduledAt))}</time>
              <div><button onClick={swap} disabled={saving}><RefreshCw size={17} /> Swap verse</button><button disabled={saving} onClick={() => mutate({ status: selected.status === 'skipped' ? 'scheduled' : 'skipped' })}><SkipForward size={17} />{selected.status === 'skipped' ? 'Restore' : 'Skip'}</button></div>
              <a className="vd-google-event" href={getGoogleCalendarUrl(selectedCalendarEvent)} target="_blank" rel="noreferrer"><CalendarDays size={16} /> Add this delivery to Google Calendar <ExternalLink size={14} /></a>
              <p className="vd-delivery-type"><Check size={16} /> Curated verse delivery</p>
            </aside> : null}
          </div>
        </> : <div className="vd-calendar-empty"><CalendarDays size={34} /><h2>No deliveries scheduled yet</h2><p>Your calendar can stay quiet until you are ready.</p><a href="/schedule">Schedule your first verse delivery</a></div>}
      </div>
    </ScreenLayout>
  );
}
